import { getToken, onMessage } from 'firebase/messaging';
import { getFirebaseMessaging } from './firebase';
import apiClient from './axios';
import { logEvent, setUserProperty } from './analytics';

const FCM_DISABLED_KEY = 'fcm_push_disabled';

export const fcmTokenManager = {
  isDisabledByUser() {
    return localStorage.getItem(FCM_DISABLED_KEY) === 'true';
  },

  // Clear token and nickname cache on logout
  clearToken() {
    localStorage.removeItem('fcm_token');
    if ('caches' in window) {
      caches.delete('fcm-nickname-v1');
    }
  },

  // Unregister device from backend, clear local state, and mark as user-disabled
  async unregister() {
    try {
      await apiClient.delete('/devices?platform=web');
    } catch (err) {
      console.error('Failed to unregister device:', err);
    }
    localStorage.setItem(FCM_DISABLED_KEY, 'true');
    this.clearToken();
  },

  // Register token with backend and clear disabled flag
  async registerToken(token) {
    await apiClient.post('/devices', { fcm_token: token, platform: 'web' });
    localStorage.setItem('fcm_token', token);
    localStorage.removeItem(FCM_DISABLED_KEY);
  },

  // Full init flow: request permission → get token → upload every login
  // Skips if user has explicitly disabled push notifications (unless force=true).
  async initializePush(onForegroundMessage, { force = false } = {}) {
    try {
      if (!force && this.isDisabledByUser()) {
        console.info('Push notifications disabled by user');
        return false;
      }

      const messaging = await getFirebaseMessaging();
      if (!messaging) {
        console.info('FCM not supported in this browser');
        return false;
      }

      const alreadyAsked = Notification.permission !== 'default';
      const permission = await Notification.requestPermission();
      // 每次登入都會走到這裡；只在第一次真正詢問時記錄結果
      if (!alreadyAsked) logEvent('push_permission_result', { granted: permission === 'granted' });
      setUserProperty('push_enabled', String(permission === 'granted'));
      if (permission !== 'granted') {
        console.info('Notification permission denied');
        return false;
      }

      const token = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      });

      if (!token) return false;

      await this.registerToken(token);

      if (onForegroundMessage) {
        onMessage(messaging, onForegroundMessage);
      }

      return true;
    } catch (error) {
      console.error('FCM initialization error:', error);
      return false;
    }
  },
};
